import { useEffect, useState, useRef, useCallback } from "react";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Logo from "../../components/Logo";
import { useLocation } from "react-router-dom";
import LangChanger from "@layout/LangChanger";
import { 
  LucideCircleUserRound,
  LucideGlobe,
  LucideZap,
  LucideRocket,
  LucidePlay,
  LucidePlug,
  LucideMonitor,
  LucideLayoutDashboard,
  LucideShieldCheck,
  LucideArrowRight,
  LucideCheckCircle,
  LucideBuilding,
  LucideBriefcase,
  LucideNetwork,
  LucideChartPie,
  LucideSettings,
  LucideSliders,
  LucideLock,
  LucideQuote,
  LucideTrendingUp,
  LucideStar,
  LucideCalendar,
  LucideMail,
  LucideChevronDown,
  LucideArrowUpRight
} from "lucide-react";
import bg from "../../assets/Lilac Waves.mp4";
import poster from "../../assets/hero-poster.jpg";

const Main = ({ openModal }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const words = ["B2B", "intelligent", "réinventé"];
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const sendPrompt = (message) => {
    alert(`🔔 Demande envoyée :\n\n"${message}"\n\n(Simulation - ici serait intégré un appel API ou un email)`);
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="noveliatech-app">
      <style>{`
        /* RESET & VARIABLES */
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        
        :root {
          --font-sans: 'Inter', system-ui, -apple-system, sans-serif;
          --color-text-primary: #0F172A;
          --color-text-secondary: #475569;
          --color-bg-main: #FFFFFF;
          --color-bg-alt: #F8FAFC;
          --color-border: #E2E8F0;
          
          --brand-dark: #0F0A1A;
          --brand-navy: #1E1B4B;
          --brand-blue: #7C3AED;
          --brand-blue-hover: #6D28D9;
          --brand-accent: #38BDF8;
          
          --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
          --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
          --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
          --shadow-glow: 0 0 20px rgba(124, 58, 237, 0.4);
        }

        body {
          font-family: var(--font-sans);
          color: var(--color-text-primary);
          background-color: var(--color-bg-main);
          line-height: 1.6;
          -webkit-font-smoothing: antialiased;
          margin: 0;
          padding: 0;
        }

        .sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border-width: 0; }

        /* UTILITIES */
        .section-center { text-align: center; }
        .bg-light { background-color: var(--color-bg-alt); }
        section { padding: 5rem 2rem; }
        .container { max-width: 1100px; margin: 0 auto; }
        .divider { height: 1px; background: var(--color-border); max-width: 1100px; margin: 0 auto; }

        /* NAVIGATION */
        .nav {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem 5%;
          background: rgba(15, 10, 26, 0.85);
          position: sticky;
          top: 0;
          z-index: 100;
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        .nav-logo { font-size: 20px; font-weight: 700; color: #fff; display: flex; align-items: center; gap: 8px; letter-spacing: -0.5px; }
        .nav-logo span { color: #A78BFA; }
        .nav-links { display: flex; gap: 2rem; color: #fff; }
        .nav-links a { font-size: 14px; font-weight: 500; color: #ffffff; text-decoration: none; cursor: pointer; transition: color 0.2s; }
        .nav-links a:hover { color: #fff; }
        .nav-cta { background: var(--brand-blue); color: #fff; border: none; padding: 10px 20px; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; transition: background 0.2s, transform 0.2s; display: inline-flex; align-items: center; gap: 8px; }
        .nav-cta:hover { background: var(--brand-blue-hover); transform: translateY(-1px); }

        /* HERO SECTION */
        .hero {
          background: radial-gradient(circle at top right, var(--brand-navy), var(--brand-dark));
          padding: 7rem 2rem 8rem;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        .hero::before { content: ''; position: absolute; top: -10%; right: -5%; width: 500px; height: 500px; border-radius: 50%; background: radial-gradient(circle, rgba(124, 58, 237, 0.25) 0%, transparent 70%); pointer-events: none; }
        .hero::after { content: ''; position: absolute; bottom: -10%; left: -5%; width: 400px; height: 400px; border-radius: 50%; background: radial-gradient(circle, rgba(56, 189, 248, 0.15) 0%, transparent 70%); pointer-events: none; }
        
        .badge { display: inline-flex; align-items: center; gap: 8px; background: rgba(124, 58, 237, 0.15); border: 1px solid rgba(124, 58, 237, 0.3); color: #C4B5FD; font-size: 13px; font-weight: 500; padding: 6px 16px; border-radius: 99px; margin-bottom: 2rem; backdrop-filter: blur(4px); }
        .badge svg { color: #A78BFA; }
        
        .hero h1 { font-size: clamp(32px, 5vw, 56px); font-weight: 700; color: #fff; line-height: 1.15; max-width: 800px; margin: 0 auto 1.5rem; letter-spacing: -1px; position: relative; z-index: 2;}
        .hero h1 span { background: linear-gradient(135deg, #A78BFA, #C084FC); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .hero p { font-size: 18px; color: #94A3B8; max-width: 650px; margin: 0 auto 2.5rem; line-height: 1.6; position: relative; z-index: 2;}
        
        .cta-group { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; position: relative; z-index: 2;}
        .btn-primary, .btn-outline { padding: 14px 32px; border-radius: 8px; font-size: 16px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: all 0.2s; border: none; }
        .btn-primary { background: var(--brand-blue); color: #fff; box-shadow: var(--shadow-md); }
        .btn-primary:hover { background: var(--brand-blue-hover); box-shadow: var(--shadow-glow); transform: translateY(-2px); }
        .btn-outline { background: rgba(255, 255, 255, 0.05); color: #fff; border: 1px solid rgba(255, 255, 255, 0.2); backdrop-filter: blur(4px); }
        .btn-outline:hover { background: rgba(255, 255, 255, 0.1); border-color: rgba(255, 255, 255, 0.4); transform: translateY(-2px); }

        /* STATS */
        .stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          background: var(--color-bg-main);
          border-bottom: 1px solid var(--color-border);
          position: relative;
          z-index: 10;
          margin-top: -2px;
        }
        .stat-item { text-align: center; padding: 2.5rem 1rem; border-right: 1px solid var(--color-border); }
        .stat-item:last-child { border-right: none; }
        .stat-num { font-size: 36px; font-weight: 700; color: var(--brand-blue); display: block; line-height: 1; margin-bottom: 8px; letter-spacing: -1px; }
        .stat-label { font-size: 14px; font-weight: 500; color: var(--color-text-secondary); text-transform: uppercase; letter-spacing: 1px; }

        /* TYPOGRAPHY */
        .section-label { font-size: 13px; font-weight: 600; color: var(--brand-blue); letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 1rem; }
        .section-title { font-size: clamp(28px, 4vw, 36px); font-weight: 700; color: var(--color-text-primary); margin-bottom: 1.25rem; line-height: 1.2; letter-spacing: -0.5px; }
        .section-sub { font-size: 18px; color: var(--color-text-secondary); max-width: 700px; line-height: 1.6; margin-bottom: 3.5rem; }

        /* PLATFORM GRID */
        .platform-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 24px; max-width: 1100px; margin: 0 auto; }
        .platform-card { background: var(--color-bg-main); border: 1px solid var(--color-border); border-radius: 16px; padding: 2rem; transition: all 0.3s ease; box-shadow: var(--shadow-sm); }
        .platform-card:hover { border-color: var(--brand-blue); box-shadow: var(--shadow-lg); transform: translateY(-4px); }
        .card-icon { width: 56px; height: 56px; border-radius: 12px; background: #F5F3FF; display: flex; align-items: center; justify-content: center; margin-bottom: 1.5rem; transition: all 0.3s; }
        .card-icon svg { width: 28px; height: 28px; color: var(--brand-blue); transition: all 0.3s; }
        .platform-card:hover .card-icon { background: var(--brand-blue); }
        .platform-card:hover .card-icon svg { color: #fff; }
        .platform-card h3 { font-size: 18px; font-weight: 600; margin-bottom: 0.75rem; color: var(--color-text-primary); }
        .platform-card p { font-size: 15px; color: var(--color-text-secondary); line-height: 1.6; }

        /* HOW IT WORKS (FEATURES) */
        .feature-row { display: flex; gap: 4rem; align-items: center; max-width: 1100px; margin: 0 auto 5rem; flex-wrap: wrap; }
        .feature-row.reverse { flex-direction: row-reverse; }
        .feature-text { flex: 1; min-width: 300px; }
        .feature-text h3 { font-size: 22px; font-weight: 700; margin-bottom: 1rem; color: var(--color-text-primary); }
        .feature-text p { font-size: 16px; color: var(--color-text-secondary); line-height: 1.6; margin-bottom: 1.5rem; }
        
        .feature-visual { flex: 1; min-width: 300px; background: var(--color-bg-alt); border: 1px solid var(--color-border); border-radius: 20px; padding: 2rem; box-shadow: var(--shadow-md); position: relative; overflow: hidden; }
        
        .visual-header { font-size: 13px; color: var(--color-text-secondary); margin-bottom: 1rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        .mini-flow { display: flex; align-items: center; gap: 12px; font-size: 13px; color: var(--color-text-secondary); margin-bottom: 12px; flex-wrap: wrap;}
        .mini-node { background: var(--color-bg-main); border: 1px solid rgba(124, 58, 237, 0.2); border-radius: 8px; padding: 10px 14px; font-size: 13px; font-weight: 500; color: var(--brand-blue); white-space: nowrap; box-shadow: var(--shadow-sm); }
        .mini-arrow { color: #94A3B8; }
        .status-box { margin-top: 20px; padding: 16px; background: rgba(124, 58, 237, 0.05); border-radius: 12px; border: 1px solid rgba(124, 58, 237, 0.1); }
        .status-title { font-size: 14px; color: var(--brand-blue); font-weight: 600; display: flex; align-items: center; gap: 6px; }
        .status-desc { font-size: 13px; color: var(--color-text-secondary); margin-top: 4px; }
        
        .config-row { display: flex; justify-content: space-between; align-items: center; font-size: 14px; padding: 12px 16px; border-radius: 8px; margin-bottom: 10px; background: var(--color-bg-main); box-shadow: var(--shadow-sm); border: 1px solid var(--color-border); }
        .config-green { border-left: 4px solid #10B981; }
        .config-blue { border-left: 4px solid var(--brand-blue); }
        .config-val-green { color: #10B981; font-weight: 600; }
        .config-val-blue { color: var(--brand-blue); font-weight: 600; }

        .perf-grid { display: flex; gap: 12px; }
        .perf-card { flex: 1; background: var(--color-bg-main); border: 1px solid var(--color-border); border-radius: 12px; padding: 16px; text-align: center; box-shadow: var(--shadow-sm); }
        .perf-val { font-size: 24px; font-weight: 700; color: var(--brand-blue); margin-bottom: 4px; display: flex; align-items: center; justify-content: center; gap: 4px; }
        .perf-val.success { color: #10B981; }
        .perf-label { font-size: 12px; color: var(--color-text-secondary); font-weight: 500; }

        .check-list { list-style: none; display: flex; flex-direction: column; gap: 12px; margin-top: 1.5rem; }
        .check-list li { font-size: 15px; color: var(--color-text-primary); display: flex; align-items: flex-start; gap: 10px; line-height: 1.5; font-weight: 500; }
        .check-list li svg { color: #10B981; width: 20px; height: 20px; flex-shrink: 0; margin-top: 2px; }

        /* SOLUTIONS GRID */
        .solutions-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 24px; max-width: 1100px; margin: 0 auto; }
        .sol-card { background: var(--color-bg-main); border: 1px solid var(--color-border); border-radius: 16px; padding: 2rem; position: relative; overflow: hidden; transition: all 0.3s; }
        .sol-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-md); border-color: var(--sol-color); }
        .sol-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px; background: var(--sol-color); }
        .sol-icon { font-size: 32px; color: var(--sol-color); margin-bottom: 1rem; }
        .sol-icon svg { width: 32px; height: 32px; color: var(--sol-color); }
        .sol-card h3 { font-size: 18px; font-weight: 600; margin-bottom: 0.5rem; color: var(--color-text-primary); }
        .sol-card p { font-size: 15px; color: var(--color-text-secondary); line-height: 1.6; }
        .sol-tag { display: inline-flex; font-size: 12px; font-weight: 600; padding: 4px 12px; border-radius: 99px; background: var(--sol-bg); color: var(--sol-text); margin-bottom: 1.25rem; letter-spacing: 0.5px; }

        /* WHY GRID */
        .why-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 32px; max-width: 1100px; margin: 0 auto; }
        .why-item { display: flex; gap: 16px; align-items: flex-start; }
        .why-icon { width: 48px; height: 48px; border-radius: 12px; background: #F5F3FF; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .why-icon svg { width: 24px; height: 24px; color: var(--brand-blue); }
        .why-item h4 { font-size: 16px; font-weight: 600; margin-bottom: 6px; color: var(--color-text-primary); }
        .why-item p { font-size: 14px; color: var(--color-text-secondary); line-height: 1.6; }

        /* REVIEWS */
        .reviews-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 24px; max-width: 1100px; margin: 0 auto; }
        .review-card { background: var(--color-bg-main); border: 1px solid var(--color-border); border-radius: 16px; padding: 2.5rem 2rem; box-shadow: var(--shadow-sm); position: relative; }
        .review-quote-icon { position: absolute; top: 1.5rem; right: 2rem; color: #F8FAFC; width: 48px; height: 48px; z-index: 0; }
        .stars { color: #F59E0B; font-size: 16px; margin-bottom: 1rem; letter-spacing: 2px; position: relative; z-index: 1;}
        .review-card blockquote { font-size: 15px; color: var(--color-text-primary); line-height: 1.7; font-style: italic; margin-bottom: 1.5rem; position: relative; z-index: 1;}
        .reviewer { display: flex; align-items: center; gap: 12px; position: relative; z-index: 1;}
        .avatar { width: 44px; height: 44px; border-radius: 50%; background: #F5F3FF; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 600; color: var(--brand-blue); flex-shrink: 0; border: 2px solid #fff; box-shadow: var(--shadow-sm); }
        .reviewer-info .name { font-size: 14px; font-weight: 600; color: var(--color-text-primary); }
        .reviewer-info .role { font-size: 13px; color: var(--color-text-secondary); }

        /* PARTNERS */
        .partner-section { padding: 3rem 2rem; text-align: center; border-bottom: 1px solid var(--color-border); }
        .partner-title { font-size: 14px; color: var(--color-text-secondary); margin-bottom: 2rem; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; }
        .partner-logos { display: flex; gap: 2.5rem; flex-wrap: wrap; justify-content: center; align-items: center; opacity: 0.6; filter: grayscale(100%); transition: opacity 0.3s, filter 0.3s; }
        .partner-logos:hover { opacity: 1; filter: grayscale(0%); }
        .partner-logo { font-size: 18px; font-weight: 700; color: var(--brand-navy); font-family: sans-serif; letter-spacing: -0.5px; }

        /* FAQ */
        .faq-list { max-width: 800px; margin: 0 auto; display: flex; flex-direction: column; gap: 12px; }
        .faq-item { background: var(--color-bg-main); border: 1px solid var(--color-border); border-radius: 12px; overflow: hidden; transition: all 0.3s; box-shadow: var(--shadow-sm); }
        .faq-item:hover { border-color: #CBD5E1; }
        .faq-q { padding: 1.25rem 1.5rem; font-size: 16px; font-weight: 600; color: var(--color-text-primary); cursor: pointer; display: flex; justify-content: space-between; align-items: center; gap: 16px; user-select: none; }
        .faq-q svg { width: 20px; height: 20px; color: var(--color-text-secondary); transition: transform 0.3s ease; flex-shrink: 0; background: #F1F5F9; border-radius: 50%; padding: 4px; }
        .faq-a { font-size: 15px; color: var(--color-text-secondary); line-height: 1.7; padding: 0 1.5rem; max-height: 0; overflow: hidden; transition: max-height 0.4s ease, padding 0.4s ease; opacity: 0; }
        
        .faq-item.open { border-color: var(--brand-blue); box-shadow: var(--shadow-md); }
        .faq-item.open .faq-q svg { transform: rotate(180deg); background: var(--brand-blue); color: #fff; }
        .faq-item.open .faq-a { max-height: 400px; padding: 0 1.5rem 1.5rem; opacity: 1; }

        /* CTA SECTION */
        .cta-section { background: radial-gradient(circle at bottom, var(--brand-navy), var(--brand-dark)); text-align: center; padding: 6rem 2rem; position: relative; }
        .cta-section .badge { border-color: rgba(255,255,255,0.2); background: rgba(255,255,255,0.05); color: #fff;}
        .cta-section h2 { font-size: clamp(28px, 4vw, 40px); color: #fff; font-weight: 700; margin-bottom: 1.25rem; letter-spacing: -0.5px; }
        .cta-section p { color: #94A3B8; font-size: 18px; margin-bottom: 2.5rem; max-width: 600px; margin-left: auto; margin-right: auto; line-height: 1.6;}

        /* FOOTER */
        .footer { padding: 2rem 5%; background: #0F0A1A; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; border-top: 1px solid rgba(255,255,255,0.1); }
        .footer-text { color: #64748B; font-size: 14px; }
        .footer-links { display: flex; gap: 2rem; }
        .footer-links span { color: #64748B; font-size: 14px; cursor: pointer; transition: color 0.2s; }
        .footer-links span:hover { color: #fff; }

        /* RESPONSIVE */
        @media (max-width: 1200px) {
          .nav-links { display: none; }
          .feature-row { gap: 2rem; }
          .stat-item { border-right: none; border-bottom: 1px solid var(--color-border); }
          .stat-item:last-child { border-bottom: none; }
          .footer { justify-content: center; flex-direction: column; text-align: center; }
        }
      `}</style>

          <h2 className="sr-only">{t('nav.platform')}</h2>

      {/* Navigation */}
      <nav className="nav">
        <div className="nav-logo">
          <Logo isWhite={true} />
          {/* <LucideGlobe size={20} aria-hidden="true" /> Novelia<span>tech</span> */}
        </div>
        <div className="nav-links">
          <a onClick={() => scrollToSection('platform')}>{t('nav.platform')}</a>
          <a onClick={() => scrollToSection('solutions')}>{t('nav.solutions')}</a>
          <a onClick={() => scrollToSection('reviews')}>{t('nav.partners')}</a>
          <a onClick={() => scrollToSection('faq')}>{t('nav.faq')}</a>
        </div>
        <div className="d-flex justify-content-end align-items-center gap-4 text-white fw-300" style={{ fontSize: "90%" }}>
          <div className="d-flex justify-content-end align-items-center gap-4 fw-300" style={{ fontSize: "90%" }}>
            <span className="d-md-block text-white">
              <LangChanger location={location} t={t} />
            </span>
            <button
              className="d-none d-lg-inline-block nav-cta"
              onClick={() => openModal("in")}
            >
              {t('nav.login')}
            </button>
            <button
              className="d-none d-lg-inline-block nav-cta"
              onClick={() => openModal("up")}
            >
              {t('nav.register')}
            </button>
            <button
              className="d-inline-block d-lg-none bg-transparent border-0 text-white fs-4"
              onClick={() => openModal("in")}
              aria-label="User menu"
            >
              <LucideCircleUserRound size={23} />
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="hero">
        <div className="container">
          <div className="badge">
            <LucideZap size={16} aria-hidden="true" /> {t('hero.badge')}
          </div>
          <h1>
            {t('hero.title')} <span>{t('hero.title_highlight')}</span>
          </h1>
          <p>{t('hero.subtitle')}</p>
          <div className="cta-group">
            <button className="btn-primary" onClick={() => openModal("up")}>
              <LucideRocket size={18} aria-hidden="true" /> {t('hero.cta_partner')}
            </button>
            <button className="btn-outline" onClick={() => sendPrompt(t('hero.cta_demo'))}>
              <LucidePlay size={18} aria-hidden="true" /> {t('hero.cta_demo')}
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="stats">
        <div className="stat-item">
          <span className="stat-num">+150</span>
          <div className="stat-label">{t('stats.partners')}</div>
        </div>
        <div className="stat-item">
          <span className="stat-num">+40</span>
          <div className="stat-label">{t('stats.countries')}</div>
        </div>
        <div className="stat-item">
          <span className="stat-num">99,9%</span>
          <div className="stat-label">{t('stats.availability')}</div>
        </div>
        <div className="stat-item">
          <span className="stat-num">&lt;200ms</span>
          <div className="stat-label">{t('stats.response_time')}</div>
        </div>
      </div>

      {/* Platform Section */}
      <section id="platform">
        <div className="container">
          <div className="section-center">
            <div className="section-label">{t('platform.label')}</div>
            <h2 className="section-title">{t('platform.title')}</h2>
            <p className="section-sub" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
              {t('platform.subtitle')}
            </p>
          </div>
          <div className="platform-grid">
            <div className="platform-card">
              <div className="card-icon"><LucidePlug size={28} aria-hidden="true" /></div>
              <h3>{t('platform.cards.automated.title')}</h3>
              <p>{t('platform.cards.automated.description')}</p>
            </div>
            <div className="platform-card">
              <div className="card-icon"><LucideMonitor size={28} aria-hidden="true" /></div>
              <h3>{t('platform.cards.manual.title')}</h3>
              <p>{t('platform.cards.manual.description')}</p>
            </div>
            <div className="platform-card">
              <div className="card-icon"><LucideLayoutDashboard size={28} aria-hidden="true" /></div>
              <h3>{t('platform.cards.commercial.title')}</h3>
              <p>{t('platform.cards.commercial.description')}</p>
            </div>
            <div className="platform-card">
              <div className="card-icon"><LucideShieldCheck size={28} aria-hidden="true" /></div>
              <h3>{t('platform.cards.security.title')}</h3>
              <p>{t('platform.cards.security.description')}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="divider"></div>

      {/* How It Works */}
      <section>
        <div className="container">
          <div className="section-center" style={{ marginBottom: '4rem' }}>
            <div className="section-label">{t('how_it_works.label')}</div>
            <h2 className="section-title">{t('how_it_works.title')}</h2>
          </div>
          
          {/* Step 1 */}
          <div className="feature-row">
            <div className="feature-text">
              <h3>{t('how_it_works.step1.title')}</h3>
              <p>{t('how_it_works.step1.description')}</p>
              <ul className="check-list">
                {t('how_it_works.step1.items', { returnObjects: true }).map((item, index) => (
                  <li key={index}>
                    <LucideCheckCircle size={20} aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="feature-visual">
              <div className="visual-header">{t('how_it_works.step1.visual.title')}</div>
              <div className="mini-flow">
                <span className="mini-node">{t('how_it_works.step1.visual.automated')}</span>
                <LucideArrowRight size={16} className="mini-arrow" />
                <span className="mini-node" style={{ borderColor: '#10B981', color: '#10B981' }}>
                  {t('how_it_works.step1.visual.hub')}
                </span>
              </div>
              <div className="mini-flow">
                <span className="mini-node">{t('how_it_works.step1.visual.manual')}</span>
                <LucideArrowRight size={16} className="mini-arrow" />
                <span className="mini-node" style={{ borderColor: '#10B981', color: '#10B981' }}>
                  {t('how_it_works.step1.visual.hub')}
                </span>
              </div>
              <div className="status-box">
                <div className="status-title">
                  <LucideCheckCircle size={18} /> {t('how_it_works.step1.visual.status')}
                </div>
                <div className="status-desc">{t('how_it_works.step1.visual.status_desc')}</div>
              </div>
            </div>
          </div>

          {/* Step 2 - Reverse */}
          <div className="feature-row reverse">
            <div className="feature-text">
              <h3>{t('how_it_works.step2.title')}</h3>
              <p>{t('how_it_works.step2.description')}</p>
              <ul className="check-list">
                {t('how_it_works.step2.items', { returnObjects: true }).map((item, index) => (
                  <li key={index}>
                    <LucideCheckCircle size={20} aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="feature-visual">
              <div className="visual-header">{t('how_it_works.step2.visual.title')}</div>
              <div className="config-row config-green">
                <span style={{ color: 'var(--color-text-secondary)' }}>
                  {t('how_it_works.step2.visual.markup')}
                </span>
                <span className="config-val-green">+12%</span>
              </div>
              <div className="config-row config-blue">
                <span style={{ color: 'var(--color-text-secondary)' }}>
                  {t('how_it_works.step2.visual.channel')}
                </span>
                <span className="config-val-blue">{t('how_it_works.step2.visual.channel_status')}</span>
              </div>
              <div className="config-row config-blue">
                <span style={{ color: 'var(--color-text-secondary)' }}>
                  {t('how_it_works.step2.visual.cache')}
                </span>
                <span className="config-val-blue">{t('how_it_works.step2.visual.cache_update')}</span>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="feature-row">
            <div className="feature-text">
              <h3>{t('how_it_works.step3.title')}</h3>
              <p>{t('how_it_works.step3.description')}</p>
              <ul className="check-list">
                {t('how_it_works.step3.items', { returnObjects: true }).map((item, index) => (
                  <li key={index}>
                    <LucideCheckCircle size={20} aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="feature-visual">
              <div className="visual-header">{t('how_it_works.step3.visual.title')}</div>
              <div className="perf-grid">
                <div className="perf-card">
                  <div className="perf-val">3 240</div>
                  <div className="perf-label">{t('how_it_works.step3.visual.bookings')}</div>
                </div>
                <div className="perf-card">
                  <div className="perf-val success">
                    <LucideTrendingUp size={16} /> 18%
                  </div>
                  <div className="perf-label">{t('how_it_works.step3.visual.growth')}</div>
                </div>
                <div className="perf-card">
                  <div className="perf-val">128 k€</div>
                  <div className="perf-label">{t('how_it_works.step3.visual.revenue')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section id="solutions" className="bg-light">
        <div className="container">
          <div className="section-center">
            <div className="section-label">{t('solutions.label')}</div>
            <h2 className="section-title">{t('solutions.title')}</h2>
            <p className="section-sub" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
              {t('solutions.subtitle')}
            </p>
          </div>
          <div className="solutions-grid">
            <div className="sol-card" style={{ '--sol-color': '#7C3AED', '--sol-bg': '#F5F3FF', '--sol-text': '#6D28D9' }}>
              <div className="sol-tag">{t('solutions.cards.extranet.tag')}</div>
              <div className="sol-icon"><LucideBuilding size={32} /></div>
              <h3>{t('solutions.cards.extranet.title')}</h3>
              <p>{t('solutions.cards.extranet.description')}</p>
            </div>
            <div className="sol-card" style={{ '--sol-color': '#06B6D4', '--sol-bg': '#ECFEFF', '--sol-text': '#0E7490' }}>
              <div className="sol-tag">{t('solutions.cards.booking.tag')}</div>
              <div className="sol-icon"><LucideBriefcase size={32} /></div>
              <h3>{t('solutions.cards.booking.title')}</h3>
              <p>{t('solutions.cards.booking.description')}</p>
            </div>
            <div className="sol-card" style={{ '--sol-color': '#8B5CF6', '--sol-bg': '#F5F3FF', '--sol-text': '#6D28D9' }}>
              <div className="sol-tag">{t('solutions.cards.connectivity.tag')}</div>
              <div className="sol-icon"><LucideNetwork size={32} /></div>
              <h3>{t('solutions.cards.connectivity.title')}</h3>
              <p>{t('solutions.cards.connectivity.description')}</p>
            </div>
            <div className="sol-card" style={{ '--sol-color': '#F59E0B', '--sol-bg': '#FFFBEB', '--sol-text': '#B45309' }}>
              <div className="sol-tag">{t('solutions.cards.financial.tag')}</div>
              <div className="sol-icon"><LucideChartPie size={32} /></div>
              <h3>{t('solutions.cards.financial.title')}</h3>
              <p>{t('solutions.cards.financial.description')}</p>
            </div>
            <div className="sol-card" style={{ '--sol-color': '#10B981', '--sol-bg': '#ECFDF5', '--sol-text': '#065F46' }}>
              <div className="sol-tag">{t('solutions.cards.api.tag')}</div>
              <div className="sol-icon"><LucideChartPie size={32} /></div>
              <h3>{t('solutions.cards.api.title')}</h3>
              <p>{t('solutions.cards.api.description')}</p>
            </div>
            <div className="sol-card" style={{ '--sol-color': '#EF4444', '--sol-bg': '#FEF2F2', '--sol-text': '#B91C1C' }}>
              <div className="sol-tag">{t('solutions.cards.support.tag')}</div>
              <div className="sol-icon"><LucideChartPie size={32} /></div>
              <h3>{t('solutions.cards.support.title')}</h3>
              <p>{t('solutions.cards.support.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Noveliatech */}
      <section>
        <div className="container">
          <div className="section-center" style={{ marginBottom: '4rem' }}>
            <div className="section-label">{t('why.label')}</div>
            <h2 className="section-title">{t('why.title')}</h2>
          </div>
          <div className="why-grid">
            <div className="why-item">
              <div className="why-icon"><LucideSettings size={24} /></div>
              <div>
                <h4>{t('why.items.freedom.title')}</h4>
                <p>{t('why.items.freedom.description')}</p>
              </div>
            </div>
            <div className="why-item">
              <div className="why-icon"><LucideSliders size={24} /></div>
              <div>
                <h4>{t('why.items.autonomy.title')}</h4>
                <p>{t('why.items.autonomy.description')}</p>
              </div>
            </div>
            <div className="why-item">
              <div className="why-icon"><LucideGlobe size={24} /></div>
              <div>
                <h4>{t('why.items.visibility.title')}</h4>
                <p>{t('why.items.visibility.description')}</p>
              </div>
            </div>
            <div className="why-item">
              <div className="why-icon"><LucideLock size={24} /></div>
              <div>
                <h4>{t('why.items.security.title')}</h4>
                <p>{t('why.items.security.description')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners */}
      <div className="partner-section">
        <div className="container">
          <div className="partner-title">{t('partners.title')}</div>
          <div className="partner-logos">
            <div className="partner-logo">HotelConnect</div>
            <div className="partner-logo">Travelia OTA</div>
            <div className="partner-logo">VoyagesPro</div>
            <div className="partner-logo">SkyLink Travel</div>
            <div className="partner-logo">TourNexus</div>
            <div className="partner-logo">AlpinRes</div>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section id="reviews" className="bg-light">
        <div className="container">
          <div className="section-center">
            <div className="section-label">{t('reviews.label')}</div>
            <h2 className="section-title">{t('reviews.title')}</h2>
            <p className="section-sub" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
              {t('reviews.subtitle')}
            </p>
          </div>
          <div className="reviews-grid">
            {t('reviews.reviews', { returnObjects: true }).map((review, index) => (
              <div className="review-card" key={index}>
                <LucideQuote className="review-quote-icon" size={48} />
                <div className="stars">{review.stars}</div>
                <blockquote>{review.quote}</blockquote>
                <div className="reviewer">
                  <div className="avatar">{review.name.split(' ').map(n => n[0]).join('')}</div>
                  <div className="reviewer-info">
                    <div className="name">{review.name}</div>
                    <div className="role">{review.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider"></div>

      {/* FAQ */}
      <section id="faq">
        <div className="container">
          <div className="section-center">
            <div className="section-label">{t('faq.label')}</div>
            <h2 className="section-title">{t('faq.title')}</h2>
            <p className="section-sub" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
              {t('faq.subtitle')}
            </p>
          </div>
          <div className="faq-list">
            {t('faq.items', { returnObjects: true }).map((item, index) => (
              <div key={index} className={`faq-item ${openFaq === index ? 'open' : ''}`}>
                <div className="faq-q" onClick={() => toggleFaq(index)}>
                  {item.question} <LucideChevronDown size={20} />
                </div>
                <div className="faq-a">{item.answer}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <div className="cta-section">
        <div className="container">
          <div className="badge">
            <LucideStar size={16} fill="#C4B5FD" /> {t('cta.badge')}
          </div>
          <h2>{t('cta.title')}</h2>
          <p>{t('cta.subtitle')}</p>
          <div className="cta-group">
            <button className="btn-primary" onClick={() => sendPrompt(t('cta.demo'))}>
              <LucideCalendar size={18} /> {t('cta.demo')}
            </button>
            <button className="btn-outline" onClick={() => sendPrompt(t('cta.contact'))}>
              <LucideMail size={18} /> {t('cta.contact')}
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-text">
          © 2026 Noveliatech — {t('footer.rights')}
        </div>
        <div className="footer-links">
          <span>{t('footer.legal')}</span>
          <span>{t('footer.privacy')}</span>
          <span>{t('footer.terms')}</span>
        </div>
      </footer>
    </div>
  );
};

export default Main;